import React, { useState, useEffect, useRef, } from 'react';
import { FormGroup, Label, InputGroup, Input, Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { uniqBy, isEmpty, sortBy, sum, groupBy, get, keyBy, omit, mapValues, } from 'lodash';
import { useDebounce, useToggle, useList, } from 'react-use';
import { toast, } from 'react-toastify';
import numeral from 'numeral';

import okSoundSrc from '../../sounds/ok.mp3';
import ngSoundSrc from '../../sounds/ng.mp3';
import firebase from '../../firebase';
import { batch, getDocumentData } from '../../shared/firebase';
import { isMobile, safePrefix, } from '../../util';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useFormState from '../hooks/useFormState';
import ProductSelectOptionLabel from '../ProductSelectOptionLabel';
import Field from '../Field';
import { idWithTenant, } from '../../shared/util';
import ListForm from '../ListForm';
import ProgressButton from '../ProgressButton';
import PendingItemForm from '../forms/PendingItemForm';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentSubscription from '../hooks/useDocumentSubscription';

const { entries, } = Object;
const db = firebase.firestore();
const inventoriesRef = db.collection('inventories');
const tenantsRef = db.collection('tenants');

export default function RevertingShelfModal(props) {
  const { isOpen, tenants, locations, onClickClose, } = props;
  const ngAudioEl = useRef(null);
  const okAudioEl = useRef(null);
  const locationsByKey = keyBy(locations, 'sortKey');
  const tenantsById = keyBy(tenants, 'id');
  const [locationKeyValue, setLocationKeyValue] = useState('');
  const matchedLocation = locationsByKey[locationKeyValue];
  const [destLocationKeyValue, setDestLocationKeyValue] = useState('');
  const [debouncedDestLocationKeyValue, setDebouncedDestLocationKeyValue] = useState('');
  const matchedInventories = useCollectionSubscription(matchedLocation && inventoriesRef.where('status', '==', 'shelved').where('locationId', '==', matchedLocation.id), [locationKeyValue]);
  const inventoriesGroupedByProductId = groupBy(matchedInventories, _ => [_.tenantId, _.productId].join('-'));
  const products = useDocumentsFetch(uniqBy(matchedInventories.map(_ => [_.tenantId, _.productId]), _ => _.join('-')).map(_ => db.doc(`tenants/${_[0]}/products/${_[1]}`)), [matchedInventories]);
  const destLocationInput = useRef(null);
  const playAudio = (audio) => {
    audio.currentTime = 0;
    audio.play();
  };
  useDebounce(() => {
    if(locationKeyValue) destLocationInput.current.focus();
  }, 300, [locationKeyValue]);
  useDebounce(() => {
    setDebouncedDestLocationKeyValue(destLocationKeyValue);
  }, 300, [destLocationKeyValue]);
  useEffect(() => {
    if(isEmpty(debouncedDestLocationKeyValue)) return;

    (async () => {
      const location = locationsByKey[debouncedDestLocationKeyValue];
      if(location == null) return playAudio(ngAudioEl.current);
      if(!window.confirm('ロケ移動します。よろしいですか？')) return;
      setLocationKeyValue('');
      setDestLocationKeyValue('');
      await batch(db, matchedInventories, (b, _) => b.update(_.ref, {
        locationId: location.id,
        locationKey: location.sortKey,
      }));
    })();

  }, [debouncedDestLocationKeyValue]);

  return (
    <Modal isOpen={isOpen}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            在庫移動
          </h5>
        </div>
      </div>
      <ModalBody>
        <FormGroup>
          <Label>ロケーション</Label>
          <Input value={locationKeyValue} onChange={_ => setLocationKeyValue(_.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>移動先ロケーション</Label>
          <Input innerRef={destLocationInput} value={destLocationKeyValue} onChange={_ => setDestLocationKeyValue(_.target.value)} />
        </FormGroup>
        {
          matchedLocation && (
            <div className="mb-2">
              合計商品点数: {numeral(matchedInventories.length).format()}
            </div>
          )
        }
        <div className="overflow-auto mb-2" style={{ maxHeight: 'calc(100vh - 400px)', }}>
          <table className="table sticky-table table-bordered">
            <thead className="thead-light text-center">
              <tr className="text-nowrap">
                <th>
                  商品ID
                </th>
                <th>
                  SKU / FNSKU
                </th>
                <th>
                  数量
                </th>
              </tr>
            </thead>
            <tbody style={{ wordBreak: 'break-all', }}>
              {
                products.map((product) => {
                  const inventories = inventoriesGroupedByProductId[idWithTenant(product.ref)];
                  return (
                    <tr key={product.id}>
                      <td>
                        {idWithTenant(product.ref)}
                      </td>
                      <td>
                        <div>
                          {product.sku}
                        </div>
                        <div>
                          {product.fnsku}
                        </div>
                      </td>
                      <td className="text-right">
                        {numeral(inventories?.length).format()}
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
      </ModalFooter>
      <audio src={ngSoundSrc} ref={ngAudioEl} />
      <audio src={okSoundSrc} ref={okAudioEl} />
    </Modal>
  );
};
